"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { motion, useInView, useReducedMotion } from "@/lib/motion";
import { inViewScrollRootMargin } from "@/lib/motion-viewport";
import { stats, UPTIME_COLS, TAB_ACCENT_GRADIENT, CANVAS_H } from "./data";
import { useUptime } from "@/hooks/use-uptime";
import { CountUp } from "./parts";
import dynamic from "next/dynamic";

const IpStreamCanvas = dynamic(() => import("./ip-stream-canvas"), {
  ssr: false,
  loading: () => <div className="w-full" style={{ height: CANVAS_H }} />,
});

// Tab 1 ("195+ Countries") uses the dot globe: a rotating particle sphere with a
// faint country map and orbiting region/targeting badges.
const DotGlobeCanvas = dynamic(() => import("./dot-globe-canvas"), {
  ssr: false,
  loading: () => <div className="w-full" style={{ height: CANVAS_H }} />,
});

// Tab 2 ("Uptime") uses the particle grid: segments settle into a status board
// with a scanner sweep; live incidents from BetterStack turn segments red.
const ParticleCanvas = dynamic(() => import("./particle-canvas"), {
  ssr: false,
  loading: () => <div className="w-full" style={{ height: CANVAS_H }} />,
});

// Tab 3 ("<0.6s Avg Response") uses the ping radar: a rotating sweep over ms rings
// where every echo lands inside the dashed 600ms boundary; the cursor drops pings too.
const LatencyCanvas = dynamic(() => import("./latency-canvas"), {
  ssr: false,
  loading: () => <div className="w-full" style={{ height: CANVAS_H }} />,
});

export default function StatsSection() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: inViewScrollRootMargin });
  const reduced = useReducedMotion();
  const [active, setActive] = useState(0);
  const activated = useRef(false);
  const tabRefs = useRef<(HTMLButtonElement | null)[]>([]);
  // Tracks whether the user has switched tabs at least once, so the camera
  // "dip" keyframes don't fire on the initial render.
  const [hasPanned, setHasPanned] = useState(false);
  const liveUptime = useUptime();

  const selectTab = (i: number) => {
    if (i !== active) setHasPanned(true);
    setActive(i);
  };

  const handleTabKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== "ArrowLeft" && e.key !== "ArrowRight") return;
    e.preventDefault();
    const dir = e.key === "ArrowRight" ? 1 : -1;
    const next = (active + dir + stats.length) % stats.length;
    selectTab(next);
    tabRefs.current[next]?.focus();
  };

  const { data: uptimeData } = useQuery<{ monitors: { status: string }[] }>({
    queryKey: ["uptime"],
    queryFn: () => fetch("/api/uptime").then((r) => r.json()),
  });

  const liveIncidents = useMemo(() => {
    const isDown = uptimeData?.monitors?.some((m) => m.status === "downtime") ?? false;
    if (!isDown) return new Set<number>();
    const s = new Set<number>();
    for (let col = UPTIME_COLS - 3; col < UPTIME_COLS; col++) s.add(col);
    return s;
  }, [uptimeData]);

  useEffect(() => {
    if (isInView && !activated.current) {
      activated.current = true;
      setActive(0);
    }
  }, [isInView]);

  return (
    <section
      ref={ref}
      /* Theme-aware band: tokens flip with the browser's color scheme, and each
         canvas resolves its palette via usePrefersLight. full-bleed makes the
         band span the whole viewport width while the content stays aligned. */
      className="full-bleed border-border bg-band hidden border-y md:block"
      aria-label="Key metrics"
    >
      <div className="mx-auto flex max-w-7xl flex-col px-4 sm:px-6 lg:px-8">
        <div
          className="divide-border border-border flex w-full divide-x"
          role="tablist"
          aria-label="Stats tabs"
          aria-orientation="horizontal"
          onKeyDown={handleTabKeyDown}
        >
          {stats.map((s, i) => {
            const on = active === i;
            const value = s.label === "Uptime" ? liveUptime : (s.value ?? "");
            return (
              <button
                key={s.label}
                ref={(el) => {
                  tabRefs.current[i] = el;
                }}
                role="tab"
                aria-selected={on}
                aria-controls={`sp-${i}`}
                id={`st-${i}`}
                type="button"
                tabIndex={on ? 0 : -1}
                onClick={() => selectTab(i)}
                onMouseEnter={() => selectTab(i)}
                onFocus={() => selectTab(i)}
                className={[
                  // flex-1 shares the row's width evenly across the four tabs
                  "relative flex min-w-0 flex-1 flex-col items-start justify-start overflow-hidden px-4 py-5 text-left lg:px-6",
                  "cursor-pointer transition-colors duration-200 outline-none",
                  on ? "bg-foreground/3" : "",
                  // Focus rings follow the PRIMARY accent (blue), not the
                  // secondary — they're an affordance, not decoration.
                  "focus-visible:ring-2 focus-visible:ring-[rgb(var(--accent-blue)/0.45)] focus-visible:ring-inset",
                  "light:focus-visible:ring-[rgb(var(--accent-blue-deep)/0.5)]",
                ].join(" ")}
              >
                {on && (
                  <motion.span
                    layoutId="tab-indicator"
                    aria-hidden="true"
                    className="pointer-events-none absolute inset-x-0 bottom-0 h-0.5"
                    style={{ background: TAB_ACCENT_GRADIENT }}
                    transition={{ type: "spring", stiffness: 180, damping: 30 }}
                  />
                )}

                {/* Tile content rises into place, staggered left to right. */}
                <motion.span
                  className="flex min-w-0 flex-col items-start"
                  initial={reduced ? false : { opacity: 0, y: 18 }}
                  animate={reduced || isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 18 }}
                  transition={{
                    duration: 0.6,
                    ease: [0.16, 1, 0.3, 1],
                    delay: reduced ? 0 : i * 0.12,
                  }}
                >
                  <span
                    className="text-3xl font-bold tracking-tight tabular-nums transition-colors duration-200"
                    style={{ color: on ? "var(--foreground)" : "var(--muted-foreground)" }}
                    aria-label={`${s.prefix ?? ""}${value}${s.suffix} ${s.label}`}
                  >
                    <CountUp
                      to={value}
                      suffix={s.suffix}
                      prefix={s.prefix}
                      decimals={s.decimals}
                      active={isInView}
                    />
                  </span>
                  <span
                    className={`mt-1 text-sm font-semibold transition-colors duration-200 ${on ? "text-foreground" : "text-muted-foreground"}`}
                  >
                    {s.label}
                  </span>
                  <span className="text-muted-foreground mt-1 text-xs leading-snug">
                    {s.description}
                  </span>
                </motion.span>
              </button>
            );
          })}
        </div>

        <div className="border-border flex min-w-0 flex-1 flex-col justify-center border-t">
          {reduced ? (
            <div
              id={`sp-${active}`}
              role="tabpanel"
              aria-labelledby={`st-${active}`}
              className="flex h-14 items-center justify-center px-6"
            >
              <span className="text-muted-foreground text-xs">{stats[active].description}</span>
            </div>
          ) : (
            /* The four canvas scenes sit side by side on one filmstrip; switching
               tabs slides the strip sideways so the active scene comes into the
               viewport, travelling in the same direction as the tab row above.
               Off-view scenes freeze their last frame; only the active scene
               keeps animating.

               Motion design: (1) the viewport dollies in (fade + settle from a
               slight zoom) when the section first scrolls into view; (2) each
               pan dips the strip to ~96.5% mid-flight and settles back, like a
               camera lifting off one scene and landing on the next; (3) scene
               focus/dim runs on the same spring family as the pan so both
               finish together. */
            <motion.div
              className="overflow-hidden"
              style={{ height: CANVAS_H }}
              initial={{ opacity: 0, scale: 1.03 }}
              animate={{ opacity: isInView ? 1 : 0, scale: isInView ? 1 : 1.03 }}
              transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            >
              {/* Four full-width tracks on one row. The grid box itself stays
                  100% wide — the tracks overflow it — so an x of -100% steps
                  the strip along by exactly one panel. */}
              <motion.div
                className="relative grid grid-cols-[100%_100%_100%_100%] will-change-transform"
                initial={{ x: "0%", scale: 1 }}
                animate={{
                  x: `${active * -100}%`,
                  scale: hasPanned ? [1, 0.965, 1] : 1,
                }}
                transition={{
                  x: { type: "spring", stiffness: 90, damping: 26, mass: 1 },
                  scale: { duration: 0.75, times: [0, 0.35, 1], ease: "easeInOut" },
                }}
              >
                <motion.div
                  id="sp-0"
                  role="tabpanel"
                  aria-labelledby="st-0"
                  aria-hidden={active !== 0}
                  className="min-w-0"
                  initial={{ opacity: 1 }}
                  animate={{ opacity: active === 0 ? 1 : 0.4 }}
                  transition={{ type: "spring", stiffness: 110, damping: 25 }}
                >
                  <IpStreamCanvas active={active === 0} />
                </motion.div>
                <motion.div
                  id="sp-1"
                  role="tabpanel"
                  aria-labelledby="st-1"
                  aria-hidden={active !== 1}
                  className="min-w-0"
                  initial={{ opacity: 0.4 }}
                  animate={{ opacity: active === 1 ? 1 : 0.4 }}
                  transition={{ type: "spring", stiffness: 110, damping: 25 }}
                >
                  <DotGlobeCanvas active={active === 1} />
                </motion.div>
                <motion.div
                  id="sp-2"
                  role="tabpanel"
                  aria-labelledby="st-2"
                  aria-hidden={active !== 2}
                  className="min-w-0"
                  initial={{ opacity: 0.4 }}
                  animate={{ opacity: active === 2 ? 1 : 0.4 }}
                  transition={{ type: "spring", stiffness: 110, damping: 25 }}
                >
                  <ParticleCanvas activeTab={2} incidents={liveIncidents} active={active === 2} />
                </motion.div>
                <motion.div
                  id="sp-3"
                  role="tabpanel"
                  aria-labelledby="st-3"
                  aria-hidden={active !== 3}
                  className="min-w-0"
                  initial={{ opacity: 0.4 }}
                  animate={{ opacity: active === 3 ? 1 : 0.4 }}
                  transition={{ type: "spring", stiffness: 110, damping: 25 }}
                >
                  <LatencyCanvas active={active === 3} />
                </motion.div>
              </motion.div>
            </motion.div>
          )}
        </div>
      </div>
    </section>
  );
}
